import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { startCheckout } from "@/lib/premium";
import { toast } from "sonner";
import { Crown, Check, X, Loader2, Sparkles, Shield } from "lucide-react";
import { PREMIUM } from "@/constants/testIds/premium";

const FEATURES = [
  { label: "Questões por dia", free: "10 / dia", premium: "Ilimitadas" },
  { label: "Simulados básicos", free: true, premium: true },
  { label: "Simulados avançados", free: false, premium: true },
  { label: "Gabarito comentado", free: true, premium: true },
  { label: "Estatísticas de desempenho", free: true, premium: true },
  { label: "Professor ApoiaConcursos IA", free: false, premium: true },
  { label: "Plano de estudos adaptativo", free: false, premium: true },
  { label: "Caderno de erros e revisão", free: false, premium: true },
  { label: "Análise de incidência", free: false, premium: true },
  { label: "Mapas mentais", free: false, premium: true },
  { label: "Materiais de estudo", free: false, premium: true },
];

export default function PremiumPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [plan, setPlan] = useState(null);
  const [plans, setPlans] = useState([]);
  const [selected, setSelected] = useState("premium_monthly");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/premium/plan").then((r) => { setPlan(r.data); setPlans(r.data.plans || []); }).catch(() => {});
    if (user?.is_premium) toast.info("Você já é assinante Premium 🎉");
  }, [user]);

  const subscribe = async () => {
    setLoading(true);
    try {
      await startCheckout(selected);
    } catch (e) {
      setLoading(false);
      toast.error(formatError(e.response?.data?.detail) || "Não foi possível abrir o checkout.");
    }
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
      <div className="mb-8 text-center vc-fade-up">
        <span className="mb-3 inline-flex items-center gap-1.5 rounded-full border border-amber-400/40 bg-amber-400/10 px-3 py-1 text-xs font-bold uppercase tracking-wide text-amber-300">
          <Sparkles className="h-3.5 w-3.5" /> ApoiaConcursos Premium
        </span>
        <h1 className="text-3xl font-extrabold text-white sm:text-4xl">Estude sem limites</h1>
        <p className="mx-auto mt-2 max-w-lg text-sm text-slate-400">
          Questões e simulados ilimitados para acelerar sua aprovação.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {/* FREE */}
        <div className="rounded-2xl border border-slate-800 bg-slate-900 p-6">
          <p className="text-sm font-bold uppercase tracking-wide text-slate-400">Grátis</p>
          <p className="mt-2 text-3xl font-extrabold text-white">R$ 0</p>
          <p className="text-xs text-slate-500">para sempre</p>
          <ul className="mt-5 space-y-3">
            {FEATURES.map((f) => (
              <li key={f.label} className="flex items-center gap-2 text-sm">
                {f.free === false ? <X className="h-4 w-4 shrink-0 text-slate-600" />
                  : <Check className="h-4 w-4 shrink-0 text-slate-400" />}
                <span className={f.free === false ? "text-slate-600 line-through" : "text-slate-300"}>
                  {f.label}{typeof f.free === "string" ? `: ${f.free}` : ""}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* PREMIUM */}
        <div className="gold-glow relative rounded-2xl border border-amber-400/40 bg-gradient-to-br from-slate-800 to-slate-900 p-6">
          <span className="absolute -top-3 right-6 rounded-full bg-amber-500 px-3 py-0.5 text-[11px] font-bold text-slate-950">MAIS POPULAR</span>
          <div className="flex items-center gap-2">
            <Crown className="h-5 w-5 text-amber-300" />
            <p className="text-sm font-bold uppercase tracking-wide text-amber-300">Premium</p>
          </div>
          <p className="mt-2 text-3xl font-extrabold text-white" data-testid={PREMIUM.planPrice}>
            {plan?.display_price || "R$ 19,90/mês"}
          </p>
          <p className="text-xs font-semibold text-emerald-400" data-testid={PREMIUM.trialBadge}>
            {plan?.trial_days ? `${plan.trial_days} dias grátis` : "7 dias grátis"} · cancele quando quiser
          </p>
          <ul className="mt-5 space-y-3">
            {FEATURES.map((f) => (
              <li key={f.label} className="flex items-center gap-2 text-sm">
                <Check className="h-4 w-4 shrink-0 text-emerald-400" />
                <span className="text-slate-100">
                  {f.label}{typeof f.premium === "string" ? `: ${f.premium}` : ""}
                </span>
              </li>
            ))}
          </ul>

          {!user?.is_premium && plans.length > 0 && (
            <div className="mt-5 grid grid-cols-2 gap-2" data-testid="premium-plan-selector">
              {plans.map((p) => (
                <button key={p.key} type="button" onClick={() => setSelected(p.key)}
                  data-testid={`premium-plan-${p.key}`}
                  className={`rounded-xl border p-2.5 text-left transition-all ${
                    selected === p.key ? "border-amber-400 bg-amber-400/10" : "border-slate-700 bg-slate-800/40 hover:border-slate-600"}`}>
                  <p className="text-xs font-bold text-white">{p.nome}</p>
                  <p className="text-[11px] text-amber-300">{p.display}</p>
                  {p.trial > 0 && <p className="text-[10px] text-emerald-400">{p.trial} dias grátis</p>}
                </button>
              ))}
            </div>
          )}

          {user?.is_premium ? (
            <div data-testid={PREMIUM.activeBadge} className="mt-6 flex items-center justify-center gap-2 rounded-xl border border-amber-400/40 bg-amber-400/10 py-3 text-sm font-bold text-amber-300">
              <Crown className="h-4 w-4" /> Premium ativo
            </div>
          ) : (
            <button
              data-testid={PREMIUM.subscribeButton}
              onClick={subscribe} disabled={loading}
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 py-3.5 text-sm font-bold text-slate-950 shadow-lg transition-all hover:from-amber-400 hover:to-amber-500 active:scale-[0.98] disabled:opacity-70">
              {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Abrindo checkout…</>
                : <><Crown className="h-4 w-4" /> Assinar Premium</>}
            </button>
          )}
          <p className="mt-3 flex items-center justify-center gap-1.5 text-[11px] text-slate-500">
            <Shield className="h-3 w-3" /> Pagamento seguro via Stripe
          </p>
        </div>
      </div>

      <button onClick={() => navigate(-1)} className="mx-auto mt-6 block text-sm font-semibold text-slate-400 hover:text-white" data-testid="premium-back">
        ← Voltar
      </button>
    </div>
  );
}
